// src/pages/services/userService.ts
import { API_BASE_URL } from "@/config";
import { getAuthHeaders } from "./authService";
import { UserProfile, UserProfileUpdate } from "@/types";
import * as mediaService from "./mediaService";

// ✅ **Register function**
export const register = async (email: string, password: string, displayName: string) => {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password, displayName }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Registration failed");
  }

  const data = await response.json();
  return data;
};

// ✅ **Login function**
export const login = async (email: string, password: string) => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Login failed");
  }

  const data = await response.json();
  return data;
};

// ✅ **Fetch user role after login**
export const getUserRole = async (accessToken: string): Promise<{ role: string } | null> => {
  const response = await fetch(`${API_BASE_URL}/auth/role`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    console.error("Failed to get user role:", response.statusText);
    return null;
  }

  return await response.json();
};

// ✅ **Logout function**
export const logout = async () => {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Logout failed");
  }
};

// ✅ **Fetch current user**
export const getCurrentUser = async () => {
  const response = await fetch(`${API_BASE_URL}/auth/user`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      return null; // Not authenticated
    }
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get current user");
  }

  const data = await response.json();
  return data.user;
};

// ✅ **Fetch user session**
export const getUserSession = async () => {
  const response = await fetch(`${API_BASE_URL}/auth/session`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      return null; // Not authenticated
    }
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get session");
  }

  const data = await response.json();
  return data.session;
};

// ✅ **Reset password**
export const resetPassword = async (email: string) => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to send reset email");
  }
};

// ✅ **Update password**
export const updatePassword = async (token: string, newPassword: string) => {
  const response = await fetch(`${API_BASE_URL}/auth/update-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token, password: newPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to update password");
  }
};

// ✅ **Auth state change handler**
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  getCurrentUser().then((user) => {
    if (user) {
      getUserSession().then((session) => {
        callback("SIGNED_IN", session);
      });
    } else {
      callback("SIGNED_OUT", null);
    }
  });

  return {
    unsubscribe: () => {},
  };
};

// ✅ **Fetch user profile**
export const getUserProfile = async (): Promise<UserProfile | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/profile`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      console.error("Error fetching user profile:", response.statusText);
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
};

// ✅ **Update user profile**
export const updateUserProfile = async (
  updates: UserProfileUpdate
): Promise<UserProfile | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/profile`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error("Error updating user profile:", errorData.message);
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating user profile:", error);
    return null;
  }
};

// ✅ **Upload avatar**
export const uploadAvatar = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/user/avatar`, {
    method: "POST",
    // Note: When uploading files, do not set Content-Type manually.
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to upload avatar");
  }

  const data = await response.json();
  return data.url;
};

// ✅ **Upload profile photo (wrapper)**
export const uploadProfilePhoto = async (file: File): Promise<string | null> => {
  try {
    // The server now returns a public URL directly, so we don't need to update the profile separately
    const url = await uploadAvatar(file);
    return url || null;
  } catch (error) {
    console.error("Error uploading profile photo:", error);
    return null;
  }
};

// ✅ **Delete profile photo**
export const deleteProfilePhoto = async (path: string): Promise<boolean> => {
  try {
    await mediaService.deleteMedia("profile-photos", path);
    return true;
  } catch (error) {
    console.error("Error deleting profile photo:", error);
    return false;
  }
};

// The following functions for follower functionality remain unchanged.
// They can be re-enabled once your database supports follower relationships.

export const followUser = async (targetUserId: string) => {
  const response = await fetch(`${API_BASE_URL}/user/follow`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ targetUserId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to follow user");
  }
};

export const unfollowUser = async (targetUserId: string) => {
  const response = await fetch(`${API_BASE_URL}/user/unfollow`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ targetUserId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to unfollow user");
  }
};

export const getFollowers = async (userId: string) => {
  const response = await fetch(`${API_BASE_URL}/user/${userId}/followers`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get followers");
  }

  return await response.json();
};

export const getFollowing = async (userId: string) => {
  const response = await fetch(`${API_BASE_URL}/user/${userId}/following`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get following");
  }

  return await response.json();
};

export const isFollowing = async (targetUserId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/is-following/${targetUserId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.isFollowing;
  } catch (error) {
    console.error("Error checking following status:", error);
    return false;
  }
};
